/**
 * Keeping a barcode readable at the till.
 *
 * No web API sets screen brightness. What the platform does give us is a wake lock, so the phone
 * does not dim mid-scan, and full control of what is on screen: a pure white field behind a pure
 * black barcode is the brightest, highest-contrast thing a display can show, which is what makes
 * the difference to a scanner reading a phone.
 */

type WakeLockSentinelLike = { release(): Promise<void>; released: boolean };

let sentinel: WakeLockSentinelLike | null = null;
let reacquire: (() => void) | null = null;

export function wakeLockSupported(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
}

/** Holds the screen awake until the returned function is called. Safe to call when unsupported. */
export async function keepScreenAwake(): Promise<() => void> {
  if (!wakeLockSupported()) return () => {};

  const request = async () => {
    try {
      const nav = navigator as unknown as { wakeLock: { request(type: 'screen'): Promise<WakeLockSentinelLike> } };
      sentinel = await nav.wakeLock.request('screen');
    } catch {
      // Denied, or the tab lost focus between the tap and here. The barcode still shows.
      sentinel = null;
    }
  };

  await request();

  // A wake lock is dropped whenever the tab is hidden — switching apps to check the queue and
  // coming back would otherwise leave the screen dimming again.
  reacquire = () => {
    if (document.visibilityState === 'visible' && (!sentinel || sentinel.released)) void request();
  };
  document.addEventListener('visibilitychange', reacquire);

  return () => {
    if (reacquire) document.removeEventListener('visibilitychange', reacquire);
    reacquire = null;
    void sentinel?.release().catch(() => {});
    sentinel = null;
  };
}

/** True when the page is running as an installed app rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; the touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}
